import React, { useRef, useState } from 'react';

import {
  Flexbox,
  FontAwesomeIcon,
  Text,
  Tooltip,
} from '@schoolinks/ui-components';
import cn from 'classnames';
import { bool, func, shape } from 'prop-types';

import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { faGear } from '@fortawesome/pro-regular-svg-icons/faGear';

import IconButton from '@mui/material/IconButton';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import {
  useArrowKeysNavigation,
  ARROW_KEYS_NAVIGATION_ITEM_CLASS,
} from 'components/Common/ArrowKeysNavigation';
import {
  GOOGLE_TRANSLATE_COOKIE_DOMAINS,
  DEFAULT_LANGUAGE_ENGLISH,
  GOOGLE_TRANSLATE_COOKIE_KEY,
  GOOGLE_TRANSLATE_COOKIE_OPTIONS,
} from 'components/Common/GoogleTranslateV2/constants';
import Avatar from 'components/Common/Layout/components/Avatar';

import features from 'core/features';
import useLocalStorage from 'hooks/useLocalStorage';
import useModal from 'hooks/useModal';
import {
  ACCOUNT_SETTINGS_ACCOUNT_ACCESS,
  ORGANIZATION_PROFILE,
  ACCOUNT_SETTINGS_AFFILIATIONS,
  SITEMAP,
} from 'routes/constants';
import getFullName from 'utils/getFullName';

import { ensureObject } from 'utils/objects';

import s from './Settings.css';

const shouldShowGoogleTranslate = (reduxState, cookieState) =>
  !!reduxState || !!cookieState;

const Settings = ({
  showGoogleTranslate,
  toggleGoogleTranslate,
  moduleAccess,
  user: {
    isCounselor,
    isHESchoolAffiliation,
    isMember,
    isDemoStudent,
    districtSettings,
    firstName,
    lastName,
    profilePic,
    preferredName,
    email,
  },
  isOnboarding,
}) => {
  // eslint-disable-next-line no-unused-vars
  const [cookies, setCookie, removeCookie] = useCookies();
  const showGoogleTranslateDropdown = shouldShowGoogleTranslate(
    showGoogleTranslate,
    cookies[GOOGLE_TRANSLATE_COOKIE_KEY],
  );
  const { i18n } = useTranslation();
  const { enableAccessibilityWidget } = ensureObject(districtSettings);
  const showAccessibilityWidgetMenuItem =
    isCounselor && enableAccessibilityWidget;

  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const settingsMenuOpen = Boolean(settingsAnchorEl);
  const menuRef = useRef(null);
  const fullName = getFullName({ firstName, lastName, preferredName });

  const [showAccessibilityWidget, setShowAccessibilityWidget] = useLocalStorage(
    'showAccessibilityWidget',
    false,
  );

  useArrowKeysNavigation({
    autoFocusOnOpen: settingsMenuOpen,
    containerRef: menuRef,
  });

  const { showModal } = useModal();

  const handleSettingsClick = event => {
    setSettingsAnchorEl(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  const handleAccessibilityWidgetItemClick = () => {
    handleSettingsClose();
    // If we want to hide the accessibility widget, refresh the page so the widget goes away
    if (showAccessibilityWidget) {
      window.location.reload();
    }
    setShowAccessibilityWidget(!showAccessibilityWidget);
  };

  const handleToggleLanguageTranslation = () => {
    handleSettingsClose();
    toggleGoogleTranslate(!showGoogleTranslate);
    if (showGoogleTranslateDropdown) {
      // Remove all googtrans cookies from all domains and paths
      GOOGLE_TRANSLATE_COOKIE_DOMAINS.forEach(domain => {
        // First try with standard options
        removeCookie(GOOGLE_TRANSLATE_COOKIE_KEY, {
          ...GOOGLE_TRANSLATE_COOKIE_OPTIONS,
          domain,
        });

        // Also try without expiration
        removeCookie(GOOGLE_TRANSLATE_COOKIE_KEY, {
          path: '/',
          domain,
        });
      });

      // Also try removing without domain)
      removeCookie(GOOGLE_TRANSLATE_COOKIE_KEY, {
        ...GOOGLE_TRANSLATE_COOKIE_OPTIONS,
      });

      i18n.changeLanguage(DEFAULT_LANGUAGE_ENGLISH);
      if (cookies[GOOGLE_TRANSLATE_COOKIE_KEY]) {
        window.location.reload();
      }
    } else {
      i18n.changeLanguage(DEFAULT_LANGUAGE_ENGLISH);
    }
  };

  return (
    <div ref={menuRef}>
      <Tooltip title="More settings">
        <IconButton
          data-testid="settings-icon"
          aria-controls={settingsMenuOpen ? 'settings-menu' : undefined}
          aria-haspopup="true"
          id="settings-menu"
          aria-expanded={settingsMenuOpen ? 'true' : undefined}
          onClick={handleSettingsClick}
          className={s.iconButton}
          aria-label="More settings"
        >
          <FontAwesomeIcon icon={faGear} />
        </IconButton>
      </Tooltip>
      <Menu
        id="fade-menu"
        disablePortal
        classes={{ root: s.menuRoot }}
        MenuListProps={{
          'aria-labelledby': 'settings-menu',
        }}
        anchorEl={settingsAnchorEl}
        open={settingsMenuOpen}
        onClose={handleSettingsClose}
      >
        <Flexbox
          p={2}
          pt={1}
          columnGap={1}
          alignItems="center"
          className={s.userInfo}
        >
          <Avatar
            role="none"
            profilePic={profilePic}
            firstName={firstName}
            lastName={lastName}
            name={fullName}
          />
          <Flexbox flexDirection="column" rowGap={0.5}>
            <Text variant="bodySm700">{fullName}</Text>
            {email && <Text variant="label">{email}</Text>}
          </Flexbox>
        </Flexbox>
        {isOnboarding ? null : (
          <MenuItem
            className={cn(s.link, ARROW_KEYS_NAVIGATION_ITEM_CLASS)}
            to={ACCOUNT_SETTINGS_ACCOUNT_ACCESS}
            component={Link}
            onClick={handleSettingsClose}
          >
            Account Settings
          </MenuItem>
        )}
        {features?.whoami ? (
          <MenuItem
            className={cn(s.link, ARROW_KEYS_NAVIGATION_ITEM_CLASS)}
            onClick={() => {
              handleSettingsClose();
              showModal('WHOAMI_MODAL');
            }}
          >
            Whoami? (Debug Info)
          </MenuItem>
        ) : null}
        {features?.controlPanel ? (
          <MenuItem
            className={cn(s.link, ARROW_KEYS_NAVIGATION_ITEM_CLASS)}
            onClick={() => {
              handleSettingsClose();
              showModal('SUPERADMIN_CONTROL_PANEL_MODAL');
            }}
          >
            Control Panel
          </MenuItem>
        ) : null}
        {showAccessibilityWidgetMenuItem && (
          <MenuItem
            className={cn(s.link, ARROW_KEYS_NAVIGATION_ITEM_CLASS)}
            onClick={handleAccessibilityWidgetItemClick}
          >
            {showAccessibilityWidget ? 'Hide' : 'Show'} accessibility widget
          </MenuItem>
        )}
        {isHESchoolAffiliation ? null : (
          <MenuItem
            className={cn(s.link, ARROW_KEYS_NAVIGATION_ITEM_CLASS)}
            onClick={handleToggleLanguageTranslation}
          >
            {showGoogleTranslateDropdown
              ? 'Hide Language Translation'
              : 'Language Translation'}
          </MenuItem>
        )}
        <MenuItem
          className={cn(s.link, ARROW_KEYS_NAVIGATION_ITEM_CLASS)}
          component={Link}
          to={SITEMAP}
          onClick={handleSettingsClose}
        >
          Sitemap
        </MenuItem>
        {/* TODO: Remove Below once linked in Side-Nav for Org Members */}
        {!!moduleAccess.orgAdministrationManagement &&
          !isHESchoolAffiliation &&
          !isCounselor && (
            <MenuItem
              className={cn(s.link, ARROW_KEYS_NAVIGATION_ITEM_CLASS)}
              component={Link}
              to={ORGANIZATION_PROFILE}
              onClick={handleSettingsClose}
            >
              Organization Profile
            </MenuItem>
          )}

        {/* TODO: to be updated (as part of org refactor) */}
        {isMember && (
          <MenuItem
            className={cn(s.link, ARROW_KEYS_NAVIGATION_ITEM_CLASS)}
            component={Link}
            to={ACCOUNT_SETTINGS_AFFILIATIONS}
            onClick={handleSettingsClose}
          >
            Manage Affiliations
          </MenuItem>
        )}
        {!(isDemoStudent && isOnboarding) && (
          <MenuItem
            className={cn(s.link, ARROW_KEYS_NAVIGATION_ITEM_CLASS)}
            component={Link}
            to="/logout"
            onClick={handleSettingsClose}
          >
            Logout
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};

Settings.propTypes = {
  toggleGoogleTranslate: func.isRequired,
  moduleAccess: shape({ orgAdministrationManagement: bool }).isRequired,
  user: shape({
    isCounselor: bool,
    isHESchoolAffiliation: bool,
    isMember: bool,
    isDemoStudent: bool,
  }).isRequired,
  isOnboarding: bool.isRequired,
  showGoogleTranslate: bool.isRequired,
};

export default Settings;
